import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { getCurrentUser, getRoleDisplayName } from '../utils/auth';

function Profile() {
  const [user, setUser] = useState(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Email
  const [email, setEmail] = useState('');

  // Signature pad
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (signatureData && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = signatureData;
    }
  }, [signatureData]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/profile');
      const profile = response.data.data;
      setUser(profile);
      setEmail(profile.email || '');
      if (profile.signature) {
        setSignatureData(profile.signature);
        setHasSignature(true);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      showMessage('Failed to load profile.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 4000);
  };

  const handleSaveEmail = async () => {
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showMessage('Please enter a valid email address.', 'error');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/profile', { email });
      showMessage('Email saved successfully.', 'success');
      updateStoredUser({ email });
    } catch (err) {
      showMessage(err.response?.data?.message || 'Failed to save email.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateStoredUser = (updates) => {
    const stored = getCurrentUser();
    if (stored) {
      const updated = { ...stored, ...updates };
      localStorage.setItem('crs_user', JSON.stringify(updated));
      setUser(updated);
    }
  };

  // Signature pad handlers
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const { x, y } = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setSignatureData(canvasRef.current.toDataURL('image/png'));
    setHasSignature(true);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
    setHasSignature(false);
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(png|jpeg|jpg)/i)) {
      showMessage('Please upload a PNG or JPG image.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSignatureData(event.target.result);
      setHasSignature(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveSignature = async () => {
    if (!signatureData) {
      showMessage('Please draw or upload a signature first.', 'error');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/profile', { signature: signatureData });
      showMessage('Signature saved successfully.', 'success');
      updateStoredUser({ signature: signatureData });
    } catch (err) {
      showMessage(err.response?.data?.message || 'Failed to save signature.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSignature = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', { signature: null });
      clearSignature();
      showMessage('Signature removed successfully.', 'success');
      updateStoredUser({ signature: null });
    } catch (err) {
      showMessage(err.response?.data?.message || 'Failed to remove signature.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      showMessage('New password must be at least 6 characters long.', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('New passwords do not match.', 'error');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      showMessage('Password changed successfully!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showMessage(err.response?.data?.message || 'Failed to change password.', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Profile</h1>
          <p className="text-muted">Manage your account information and saved signature</p>
        </div>
      </div>

      {message && (
        <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      <div className="profile-grid">
        {/* Account Information */}
        <div className="card profile-card">
          <h3 className="profile-section-title">Account Information</h3>
          <div className="profile-info-grid">
            <div className="profile-info-item">
              <label>Full Name</label>
              <p>{user?.full_name || '-'}</p>
            </div>
            <div className="profile-info-item">
              <label>Username</label>
              <p>{user?.username || '-'}</p>
            </div>
            <div className="profile-info-item">
              <label>Role</label>
              <p>{getRoleDisplayName(user?.role)}</p>
            </div>
            <div className="profile-info-item">
              <label>Branch</label>
              <p>{user?.company_name || user?.company_code || '-'}</p>
            </div>
          </div>
        </div>

        {/* Email */}
        <div className="card profile-card">
          <h3 className="profile-section-title">Email</h3>
          <div className="form-group">
            <label htmlFor="profile-email" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Email Address
            </label>
            <input
              id="profile-email"
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSaveEmail}
            disabled={saving}
            style={{ backgroundColor: '#e63740', borderColor: '#e63740', marginTop: '10px' }}
          >
            {saving ? 'Saving...' : 'Save Email'}
          </button>
        </div>

        {/* Signature Pad */}
        <div className="card profile-card" style={{ gridColumn: '1 / -1' }}>
          <h3 className="profile-section-title">Signature Pad</h3>
          <p className="text-muted" style={{ marginBottom: '16px' }}>
            Draw your signature or upload a signature image, then click Save. This signature will be auto-filled when you approve requests.
          </p>

          <div className="signature-canvas-wrapper">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="signature-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '200px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'crosshair',
                touchAction: 'none'
              }}
            />
          </div>

          <div className="signature-actions" style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={clearSignature}>
              Clear
            </button>
            <label className="btn btn-secondary" style={{ cursor: 'pointer', margin: 0 }}>
              Upload JPG/PNG
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleSignatureUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              className="btn btn-primary"
              onClick={handleSaveSignature}
              disabled={saving || !signatureData}
              style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
            >
              {saving ? 'Saving...' : 'Save Signature'}
            </button>
            {hasSignature && (
              <button
                className="btn btn-danger"
                onClick={handleRemoveSignature}
                disabled={saving}
                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
              >
                Remove Saved Signature
              </button>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="card profile-card" style={{ gridColumn: '1 / -1' }}>
          <h3 className="profile-section-title">Change Password</h3>
          <form onSubmit={handleChangePassword}>
            <div className="profile-password-grid">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={changingPassword}
              style={{ backgroundColor: '#e63740', borderColor: '#e63740', marginTop: '16px' }}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile;
